import { Modal, Platform, Pressable, View, type ViewStyle } from "react-native";
import type { ReactNode } from "react";

const DRAWER_WIDTH = 280;

/**
 * Slide-in mobile drawer used by every portal shell when the viewport
 * collapses below the responsive breakpoint. Renders nothing when
 * `open === false`. The shell passes its existing sidebar JSX as
 * children — the drawer doesn't know anything about the shell's nav
 * structure, it just provides a backdrop, a panel, and an
 * `onRequestClose` hook that nav-row presses thread back into.
 */
export function ShellMobileDrawer({
  open,
  onRequestClose,
  children,
  backgroundColor,
}: {
  open: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  /** Sidebar surface color — passed in so each portal keeps its palette. */
  backgroundColor: string;
}) {
  return (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={onRequestClose}
    >
      {/* Backdrop — tap anywhere outside the panel to close. */}
      <Pressable
        onPress={onRequestClose}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.55)",
        }}
        accessibilityLabel="Close navigation drawer"
      />
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: DRAWER_WIDTH,
          maxWidth: "85%",
          backgroundColor,
          borderRightWidth: 1,
          borderRightColor: "rgba(181,135,58,0.18)",
          flexDirection: "column",
          shadowColor: "#000",
          shadowOffset: { width: 4, height: 0 },
          shadowOpacity: 0.45,
          shadowRadius: 18,
        } as ViewStyle}
      >
        {children}
      </View>
    </Modal>
  );
}

/**
 * Topbar hamburger button — three short horizontal bars rendered as
 * stacked View "lines" so we don't depend on an icon font. Sized for a
 * comfortable 44x44 tap target even though the visible glyph is smaller.
 */
export function HamburgerButton({
  onPress,
  color,
}: {
  onPress: () => void;
  color: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Open navigation"
      hitSlop={8}
      style={[
        {
          width: 44,
          height: 44,
          alignItems: "center",
          justifyContent: "center",
          marginRight: 4,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ width: 20, gap: 4 }}>
        <View style={{ height: 1.5, backgroundColor: color, borderRadius: 1 }} />
        <View style={{ height: 1.5, backgroundColor: color, borderRadius: 1 }} />
        <View style={{ height: 1.5, backgroundColor: color, borderRadius: 1 }} />
      </View>
    </Pressable>
  );
}
